import React, { useMemo, useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Box,
    Container,
    Typography,
    Stack,
    ButtonBase,
    Chip,
} from "@mui/material";
import SectionTitle from "../components/SectionTitle";
import TeamModal from "../components/TeamModal";
import mockTeam from "../data/mockTeam";

gsap.registerPlugin(ScrollTrigger);

export default function Equipe() {
    const team = useMemo(() => (mockTeam ? mockTeam.slice(0, 9) : []), []);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef([]);

    cardsRef.current = [];

    const brandColor = "#acc90e";
    const gapSize = 24;

    function handleOpen(member) {
        setSelected(member);
        setOpen(true);
    }

    function handleClose() {
        setOpen(false);
        setSelected(null);
    }

    useEffect(() => {
        if (!sectionRef.current || cardsRef.current.length === 0) return;

        const ctx = gsap.context(() => {
            gsap.fromTo(
                titleRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.9,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 80%",
                        once: true,
                    },
                }
            );

            gsap.fromTo(
                cardsRef.current,
                { opacity: 0, y: 60 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.9,
                    ease: "power3.out",
                    stagger: 0.12,
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 75%",
                        once: true,
                    },
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <Box
            ref={sectionRef}
            id="equipe"
            sx={{
                py: { xs: 8, sm: 9, md: 10, lg: 11 },
                bgcolor: "#000",
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    px: { xs: 2, md: 3 },
                    maxWidth: 1600,
                    mx: "auto",
                }}
            >
                <Box
                    ref={titleRef}
                    sx={{
                        maxWidth: { xs: "100%", md: 980 },
                        width: "100%",
                        mb: { xs: 4, md: 5 },
                    }}
                >
                    <SectionTitle
                        eyebrow="equipe"
                        title="Quem constrói o olhar da Lusco"
                        subtitle="Direção, captação, edição, fotografia e linguagem visual reunidas para transformar energia em imagem."
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: { xs: "center", md: "flex-start" },
                        gap: `${gapSize}px`,
                    }}
                >
                    {team.map((m) => (
                        <ButtonBase
                            ref={(el) => el && cardsRef.current.push(el)}
                            key={m.id}
                            onClick={() => handleOpen(m)}
                            sx={{
                                width: {
                                    xs: "100%",
                                    sm: `calc(50% - ${gapSize / 2}px)`,
                                    md: `calc(50% - ${gapSize / 2}px)`,
                                    lg: `calc(33.333% - ${(gapSize * 2) / 3}px)`,
                                },
                                maxWidth: {
                                    xs: "100%",
                                    sm: "unset",
                                },
                                display: "block",
                                textAlign: "left",
                                position: "relative",
                                overflow: "hidden",
                                aspectRatio: {
                                    xs: "1 / 1.08",
                                    sm: "1 / 1",
                                    md: "1 / 1",
                                },
                                borderRadius: { xs: 2.5, md: 0 },
                                transition:
                                    "transform 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease",
                                border: "1px solid rgba(255,255,255,0.08)",
                                backgroundColor: "rgba(255,255,255,0.02)",

                                "&:hover": {
                                    transform: "translateY(-8px)",
                                    boxShadow: {
                                        xs: "0 18px 50px rgba(0,0,0,0.28)",
                                        md: `10px 10px 0px ${brandColor}`,
                                    },
                                    zIndex: 2,
                                },
                                "&:hover .member-photo": {
                                    transform: "scale(1.1)",
                                    filter: "grayscale(0%)",
                                },
                                "&:hover .member-info": {
                                    opacity: 1,
                                },
                            }}
                        >
                            <Box
                                className="member-photo"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundImage: `url(${m.avatarUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    transition:
                                        "transform 0.6s cubic-bezier(0.25, 1, 0.5, 1), filter 0.4s ease",
                                    filter: "grayscale(100%)",
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background:
                                        "linear-gradient(0deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%)",
                                    opacity: 0.9,
                                }}
                            />

                            <Stack
                                className="member-info"
                                justifyContent="flex-end"
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    p: { xs: 2.25, sm: 2.5, md: 3 },
                                    transition: "opacity 0.3s ease",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 4,
                                        bgcolor: brandColor,
                                        mb: { xs: 1.5, md: 2 },
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        color: "#fff",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        lineHeight: 1,
                                        mb: 0.5,
                                        fontSize: {
                                            xs: "1.35rem",
                                            sm: "1.4rem",
                                            md: "1.55rem",
                                        },
                                    }}
                                >
                                    {m.name}
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "rgba(255,255,255,0.7)",
                                        fontSize: { xs: "0.88rem", md: "0.9rem" },
                                        fontWeight: 500,
                                        mb: { xs: 1.6, md: 2 },
                                    }}
                                >
                                    {m.role}
                                </Typography>

                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {m.tags?.slice(0, 3).map((tag) => (
                                        <Chip
                                            key={tag}
                                            label={tag}
                                            size="small"
                                            sx={{
                                                borderRadius: 0,
                                                bgcolor: "rgba(255,255,255,0.08)",
                                                color: "#fff",
                                                border: "1px solid rgba(255,255,255,0.15)",
                                                fontWeight: 600,
                                                fontSize: "0.7rem",
                                                height: 24,
                                                maxWidth: "100%",
                                                "& .MuiChip-label": {
                                                    px: 1,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </ButtonBase>
                    ))}
                </Box>

                <TeamModal open={open} onClose={handleClose} member={selected} />
            </Container>
        </Box>
    );
}